'use client';

import { Box, Divider, SystemIcon, Typography } from '@wishket/design-system';
import { Fragment, Suspense } from 'react';
import dynamic from 'next/dynamic';

import { Link, Skeleton } from '@/shared/Components';
import { useAuthorProfileArticleList } from '@/entities/author';
import { PATH } from '@/shared/constants';
import { useGetThreePopularArticles } from '@/entities/article';
import { useDeviceCheck } from '@/shared/hooks';

const SideRecentPostCardClient = dynamic(
  () => import('@/features/SideRecentPostCard')
);

interface SideRecentPostsProps {
  articleId: string;
  authorId: number;
  urlIdentifier: string;
}

const SideRecentPosts = ({
  articleId,
  authorId,
  urlIdentifier,
}: SideRecentPostsProps) => {
  const { data: articles } = useAuthorProfileArticleList({
    authorId,
    articleId: Number(articleId),
  });
  const { data: popularArticles } = useGetThreePopularArticles(articleId);
  const { isLoading, isDesktop } = useDeviceCheck();

  if (!isDesktop || isLoading) return null;

  if (!articles) return <Skeleton className="h-[400px] w-[248px]" />;

  const anyOtherArticlesExist = !(articles.length > 0);
  const topThreeArticles = articles.slice(0, 3);
  const organizedTitle = anyOtherArticlesExist
    ? '인기 있는 콘텐츠'
    : '작가의 다른 콘텐츠';
  const organizedHref = anyOtherArticlesExist
    ? `${PATH.LIST_POPULAR}`
    : `${PATH.HOME}/@${urlIdentifier}`;

  return (
    <Suspense fallback={<Skeleton className="h-[400px] w-[248px]" />}>
      <Box className="flex w-full flex-col gap-5 rounded-2xl border border-w-gray-200 bg-w-white p-5">
        <Link
          href={organizedHref}
          className="flex items-center justify-between"
        >
          <Typography variant="body1" className="font-medium text-w-gray-900">
            {organizedTitle}
          </Typography>
          <SystemIcon name="medium_arrow_right" />
        </Link>
        <Box className="flex flex-col gap-4">
          {anyOtherArticlesExist
            ? popularArticles?.map((article, index) => {
                return (
                  <Fragment key={article.id}>
                    {index !== 0 && <Divider isSub />}
                    <SideRecentPostCardClient
                      category={article.category}
                      categoryFlag={article?.categoryFlag}
                      title={article.title}
                      href={article.href}
                    />
                  </Fragment>
                );
              })
            : topThreeArticles.map((article, index) => {
                return (
                  <Fragment key={article.id}>
                    {index !== 0 && <Divider isSub />}
                    <SideRecentPostCardClient
                      category={article.category}
                      categoryFlag={article.categoryFlag}
                      title={article.title}
                      href={article.href}
                    />
                  </Fragment>
                );
              })}
        </Box>
      </Box>
    </Suspense>
  );
};

export default SideRecentPosts;
